import { useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { WorkflowTabs, type WorkflowTab } from '@/components/WorkflowTabs'
import { GeneratorFrame, PriceConfirmDialog, StudioPanel, SubmitBar } from '@/components/studio'
import { Badge, Card, CardBody, CardHead, CountInput, Field, RatioInput, Segmented, Textarea } from '@/components/ui'
import { RefUploader } from '@/components/RefUploader'
import { ModelSelect } from '@/components/ModelSelect'
import { IMAGE_TOOLS, IMAGE_TOOL_MODELS, IMAGE_TOOL_RESOLUTIONS, modelLabel } from '@/config/catalog'
import { buildToolPrompt, type ToolInput } from '@/config/prompts'
import { useJobCenter, type Job } from '@/context/JobCenterContext'
import { useToast } from '@/context/ToastContext'
import { useDraft } from '@/hooks/useDraft'
import { useGenerateFlow } from '@/hooks/useGenerateFlow'
import { useLocalStorage } from '@/hooks/useLocalStorage'
import { usePricePreview } from '@/hooks/usePricePreview'
import { refillFromInputs } from '@/lib/refill'

type FormState = ToolInput & { model: string; refs: string[]; resolution: string; outputs: number }

const DEFAULTS: FormState = {
  tool: 'remove-bg',
  requirement: '',
  aspectRatio: '1:1',
  free: false,
  model: IMAGE_TOOL_MODELS['remove-bg'],
  refs: [],
  /*
    默认 2K：这一页有大半的工具（超清放大 / 换背景 / 精修）的产出是**要上架的成片**，
    1K 交不了差。系数真源在后端 `pricing.RESOLUTION_MULTIPLIER`（2K = ×1.2）。

    ⚠️ 这个字段必须**同时**写进报价、确认弹窗与提交载荷。上一版只在报价与弹窗里
    硬写了 `resolution: '2k'`，载荷里没有 —— 后端 `_normalize_resolution(None)`
    回落到「1K、系数 1.0」，于是**报价按 ¥0.12、实际按 ¥0.10 跑 1K**，
    而弹窗上还写着「分辨率 2k」。三处各说各话，一处都不报错。
  */
  resolution: '2k',
  /*
    输出张数 —— 2026-10-10 用户口径「这些出图数量 1-4，然后都可以自定义数量」。
    上一版张数是**每个工具写死**的（`tool.outputs`，如去背景=1），用户想要多出几张
    根本没有入口。现在放进表单：换工具时按该工具的默认张数重置（见两处 effect 与
    `selectTool`），之后用户可以自己调。取值在同源的报价 / 弹窗 / 载荷三处都读 `form.outputs`。
  */
  outputs: 1,
}

/**
 * 三个页签 + 常驻提交栏。
 *
 * 2026-09-29 用户要求「/img-localize 分 3 步」—— 原先 2 个页签把
 * 「选工具 / 传原图 / 调参数 / 挑模型」四件事全挤在第一个页签里，
 * 左栏一长操作就难用（这正是全站从扁平档改回页签的那条理由）。
 * 现在按**决策域**切，与项目铁律同一口径：
 *   1 选择工具   —— 输入物：这次要做什么处理
 *   2 原图与参数 —— 视觉决策：给什么原图、调什么参数、用哪个模型
 *   3 核对与生成 —— 花钱与确认
 *
 * ⚠️ 这**不是**回到「分步向导」（Stepper 已于 2026-09-28 被用户否决并删除）。
 * 两者的区别不在「几步」，而在这两条：
 *   · 页签可以任意点、随时回看；向导只能「上一步 / 下一步」线性走；
 *   · 报价与生成按钮在**右栏顶部常驻**（2026-10-08 起全站统一到这里），
 *     任何一步都看得见 —— 而向导把
 *     「花钱型参数」和「报价」拆到了两个屏幕（第 1 步看不到价格、
 *     第 2 步看不到生成按钮），必须走完流程才知道要花多少钱。
 *
 * 工具切换会把页面拉回第一个页签，并把模型换成该工具的预设模型 ——
 * 「传完图顺手点生成，用的是上一个工具残留的模型」这个真 bug 靠这条规则防住，
 * 不靠「逼用户多点一次下一步」。
 *
 * ⚠️ 这条规则**曾经是假的**：预设表里只覆盖了 3 个工具（当年共 12 个），
 * 其余 9 个走 `?? form.model`，切过去沿用的就是上一个工具的模型 ——
 * 规则写在注释里，实现却没跟上（2026-10-05 修）。现在预设表
 * `IMAGE_TOOL_MODELS` 六档全覆盖，且**没有兜底分支**：
 * 漏一档就编译/冒烟报错，不会静默退化回「沿用旧值」。
 */
const PANES: WorkflowTab[] = [
  { id: 'tool', title: '选择工具', desc: `从 ${IMAGE_TOOLS.length} 个单图工具里挑一个` },
  { id: 'media', title: '原图与参数', desc: '上传原图，设置处理参数与输出模型' },
  { id: 'generate', title: '核对与生成', desc: '参数核对后提交，失败不扣费' },
]

export function ImageTools() {
  const [sp, setSp] = useSearchParams()
  const { value: form, patch, reset } = useDraft('img-localize', DEFAULTS)
  // 步骤进度持久化，中途切页回来不丢位置。
  // ⚠️ 页签数从 2 改成 3 时必须升 key：老值里的 1 指的是「核对与生成」，
  // 在新结构里 1 变成了「原图与参数」—— 沿用老 key 的话，用户进来会停在
  // 第二步却以为只是切了个页签，而缺的那一步（传原图）永远看不到。
  const [step, setStep] = useLocalStorage<number>('step_img_localize_v2', 0)
  const { cancel, jobsFor, outputsFor, clearResults } = useJobCenter()
  // 生成成功后自动清空工作区（表单 + 上传的原图 + 右栏本轮产出）—— 见 GenerateFlowOptions.workspace
  const flow = useGenerateFlow({ workspace: { snapshot: form, reset } })
  const toast = useToast()

  const toolParam = sp.get('tool')
  useEffect(() => {
    const valid = IMAGE_TOOLS.find((t) => t.id === toolParam)
    if (valid && valid.id !== form.tool) {
      patch({ tool: valid.id, model: IMAGE_TOOL_MODELS[valid.id], outputs: valid.outputs })
      setStep(0)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toolParam])

  /*
    草稿里可能残留**已删工具**的 id（2026-10-05 这一版从 12 个砍到 6 个，
    老草稿里会带着 `upscale-2k` / `color-grade` / `add-watermark` 这类值）。

    不修的话 `tool` 会静默回落到 `IMAGE_TOOLS[0]`：界面显示「AI 去背景」，
    而 `form.tool` 还是一个不存在的 id —— 提交走的是 `tool.id`（对的），
    但 `IMAGE_TOOL_MODELS[form.tool]` 取值会踩空、`?tool=` 深链比对也会失真。
    与 `ModelSelect` 处理「草稿里残留旧 slug」是同一条做法：
    **回落到第一个可用项并回写草稿**，不留孤儿值。
  */
  useEffect(() => {
    if (IMAGE_TOOLS.some((t) => t.id === form.tool)) return
    const want = IMAGE_TOOLS.find((t) => t.id === toolParam) ?? IMAGE_TOOLS[0]
    patch({ tool: want.id, model: IMAGE_TOOL_MODELS[want.id], outputs: want.outputs })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.tool])

  const tool = IMAGE_TOOLS.find((t) => t.id === form.tool) ?? IMAGE_TOOLS[0]
  const prompt = useMemo(() => buildToolPrompt({ ...form, tool: tool.id }), [form, tool.id])

  const price = usePricePreview({
    appSlug: `tool-${tool.id}`,
    model: form.model,
    outputs: form.outputs,
    resolution: form.resolution,
    aspectRatio: form.aspectRatio,
  })

  // 第二参数收窄到**当前工具**：6 个工具的 `sourcePage` 相同、`app_slug` 不同
  // （`tool-remove-bg` / `tool-upscale` …）。不收窄的话，切到「放大」时右栏
  // 会把「去背景」的结果一起铺在标题「放大 · 结果」下面 —— 2026-09-29
  // 用户报障的同源问题（先在商品图页出现）。
  const jobs = jobsFor('img-localize', `tool-${tool.id}`)
  const outputs = outputsFor('img-localize', `tool-${tool.id}`)
  const ready = form.refs.length >= tool.needImage

  const selectTool = (id: string) => {
    // 预设模型六档全覆盖（`IMAGE_TOOL_MODELS`，冒烟钉死），所以**没有 `?? form.model` 兜底** ——
    // 那个兜底正是「切工具后沿用上一个工具残留模型」的来源。
    // 张数同理按工具重置：模型与张数一起跟着工具走，用户在当前工具里改的张数
    // 不该「粘」到下一个工具上（去背景默认 1 张，放大默认 1 张，各不相同）。
    const next = IMAGE_TOOLS.find((t) => t.id === id)
    patch({ tool: id, model: IMAGE_TOOL_MODELS[id], outputs: next?.outputs ?? form.outputs })
    setSp({ tool: id }, { replace: true })
    setStep(0)
  }

  const doGenerate = () =>
    flow.request({
      info: {
        title: tool.name,
        appSlug: `tool-${tool.id}`,
        model: form.model,
        outputs: form.outputs,
        aspectRatio: form.aspectRatio,
        resolution: form.resolution,
        refImages: form.refs.length,
        prompt,
        price: price?.estimatedPrice ?? 0,
        basis: price?.basis,
        free: price?.isFreeThisCall,
      },
      args: {
        appSlug: `tool-${tool.id}`,
        title: tool.name,
        sourcePage: 'img-localize',
        model: form.model,
        prompt,
        inputs: { ...form, __title: tool.name },
        imageUrls: form.refs,
        aspectRatio: form.aspectRatio,
        // 与报价、确认弹窗**同一份值**。少这一行就是「报价 2K、实跑 1K」。
        resolution: form.resolution,
        outputs: form.outputs,
        estimatedMs: tool.estimatedMs,
      },
    })

  /* ------------------------- 页签 1 · 选择工具 ------------------------- */

  const stepTool = (
    <Card>
      <CardHead title="选择工具" icon="wand" />
      <CardBody tight>
        <div className="tool-grid">
          {IMAGE_TOOLS.map((t) => (
            <button key={t.id} type="button" className="tool" data-active={tool.id === t.id} onClick={() => selectTool(t.id)}>
              <div className="tool__name">{t.name}</div>
              <div className="tool__desc">{t.description}</div>
            </button>
          ))}
        </div>
      </CardBody>
    </Card>
  )

  /* ------------------------- 页签 2 · 原图与参数 ------------------------- */

  const stepMedia = (
    <Card>
      <CardHead
        title={tool.name}
        icon="image"
        right={<Badge tone="info">{form.outputs} 张输出</Badge>}
      />
      <CardBody>
        <RefUploader
          wrap="none"
          label="原图"
          required
          fieldHint={`预计 ${Math.round(tool.estimatedMs / 1000)} 秒完成`}
          urls={form.refs}
          onChange={(refs) => patch({ refs })}
          max={1}
          kind="image"
          dropLabel="拖拽或点击上传原图"
          dropHint="建议上传最大尺寸的原图，放大类工具效果更好"
        />

        {tool.promptLabel ? (
          <Field label={tool.promptLabel}>
            <Textarea
              value={form.requirement}
              rows={3}
              placeholder={tool.promptPlaceholder}
              onChange={(e) => patch({ requirement: e.target.value })}
            />
          </Field>
        ) : null}

        {/*
          这里原来还有两块**按工具分支**的参数控件，都删了：

          · `add-watermark` 的「水印位置」Segmented —— 工具本身已下架（见 catalog.ts
            的说明），跟着删。
          · `expand` 的「目标比例」Segmented —— 它和下面「模型与输出」卡里的
            「输出比例」**绑的是同一个 `form.aspectRatio`，而且两块在同一个页签里
            同时渲染**：改一个另一个跟着变，两个标题、两套候选（3 档 vs 4 档）。
            用户看到的是「同一件事问了两遍，还给出不同的选项」。
            同一字段只留一个入口 —— 扩图要的就是输出画布的尺寸，共用一个「输出比例」。
        */}
      </CardBody>
    </Card>
  )

  /* ------------------------- 页签 2 · 模型与输出 ------------------------- */

  /**
   * 模型与输出（与上传原图同页签：模型决定单价，属于「视觉决策 + 花钱」，
   * 和原图放一起才谈得清「这张图要怎么处理」）。
   *
   * 这里原来还有个 callout 复述「{tool.name} · N 张输出 · 预计 X 秒」——
   * 三份同样的信息在同一个页签里：卡片标题上写了工具名、工具卡上有 outputs 的 Badge、
   * 上传框的 fieldHint 写了预计秒数。**同一字段只留一个入口**，callout 已删。
   */
  const stepConfig = (
    <Card>
      <CardHead title="模型与输出" icon="bolt" />
      <CardBody>
        <ModelSelect kind="image" value={form.model} onChange={(v) => patch({ model: v })} hasRef={form.refs.length > 0} />
        <Field label="输出比例">
          {/* 比例 = 下拉（这四种最常用）+ 自由输入框（想要别的直接敲）。
              2026-10-10 用户口径「这些……都可以自定义比例」—— 上一版是纯档位按钮组，
              想出的比例不在其中时没有入口。控件是 `RatioInput`，与视频页那份同一实现。 */}
          <RatioInput
            value={form.aspectRatio}
            onChange={(v) => patch({ aspectRatio: v })}
            options={['1:1', '3:4', '16:9', '9:16']}
          />
        </Field>
        <Field label="输出数量" hint={`本工具默认 ${tool.outputs} 张`}>
          {/* 数量 = 常用档位（1-4）+ 自定义：2026-10-10 用户口径「出图数量 1-4，
              然后都可以自定义数量」。上一版是每个工具写死的张数、没有入口，
              想多出几张只能自己想办法。 */}
          <CountInput
            value={form.outputs}
            onChange={(n) => patch({ outputs: n })}
            presets={[1, 2, 3, 4]}
          />
        </Field>
        {/*
          分辨率：原来**没有这个控件**，但报价和确认弹窗都硬写着 `'2k'` ——
          于是出现「报价按 2K（×1.2，¥0.12）、弹窗写 2k、实际按 1K 跑（¥0.10）」。
          补上控件 = 让这个参数**有界面入口**，三处才谈得上一致
          （见 `DEFAULTS.resolution` 上面的说明）。
        */}
        <Field label="分辨率" hint="放大类工具建议 2K 及以上，1K 只适合打样">
          <Segmented
            value={form.resolution}
            onChange={(v) => patch({ resolution: v })}
            options={IMAGE_TOOL_RESOLUTIONS.map((r) => ({ value: r, label: r.toUpperCase() }))}
          />
        </Field>
      </CardBody>
    </Card>
  )

  /* ------------------------- 页签 2 · 核对与生成 ------------------------- */

  const stepGenerate = (
    <Card>
      <CardHead title="参数核对" icon="check" />
      <CardBody tight>
        <div className="review-list">
          <div className="review-list__row">
            <span className="review-list__k">工具</span>
            <span className="review-list__v">
              {tool.name}
              <span className="muted">
                {' '}
                · {form.outputs} 张 · 约 {Math.round(tool.estimatedMs / 1000)} 秒
              </span>
            </span>
          </div>
          <div className="review-list__row">
            <span className="review-list__k">原图</span>
            <span className={`review-list__v${form.refs.length ? '' : ' review-list__v--muted'}`}>
              {form.refs.length ? `已上传 ${form.refs.length} 张` : '未上传'}
            </span>
          </div>
          {tool.promptLabel ? (
            <div className="review-list__row">
              <span className="review-list__k">{tool.promptLabel}</span>
              <span className={`review-list__v${form.requirement.trim() ? '' : ' review-list__v--muted'}`}>
                {form.requirement.trim() || '未填写'}
              </span>
            </div>
          ) : null}
          <div className="review-list__row">
            <span className="review-list__k">模型与输出</span>
            <span className="review-list__v">
              {modelLabel(form.model)}
              {/* 比例与分辨率都写出来 —— 报价里那个 2K 系数就是从分辨率来的，
                  核对页上看不到它，用户就对不上「为什么是 ¥0.12」 */}
              <span className="muted">
                {' '}
                · {form.aspectRatio} · {form.resolution.toUpperCase()}
              </span>
            </span>
          </div>
        </div>
      </CardBody>
    </Card>
  )

  // 分支数必须与 PANES 的页签数一致（冒烟里钉死了）——多一个就会「点后面的页签显示错内容」
  const stepBody = [stepTool, <>{stepMedia}{stepConfig}</>, stepGenerate][Math.min(step, PANES.length - 1)]

  /* ------------------------------ 底部导航 ------------------------------ */

  const submitDock = (
    <Card className="studio__dock">
      <CardBody tight>
        <SubmitBar
          disabled={!ready}
          disabledReason={!ready ? '请先在「原图与参数」上传原图' : undefined}
          label={tool.name}
          onGenerate={doGenerate}
        />
      </CardBody>
    </Card>
  )

  return (
    <div className="page page--wide">
      <GeneratorFrame
        form={
          <WorkflowTabs tabs={PANES} current={step} onChange={setStep}>
            {stepBody}
          </WorkflowTabs>
        }
        panel={
          <>
            {submitDock}
            <StudioPanel
              jobs={jobs}
              outputs={outputs}
              onCancel={cancel}
              onClear={clearResults}
              resultTitle={`${tool.name} 结果`}
              /* 历史与结果同源收窄（见上面 jobsFor 的第二参数），标题跟着当前工具走 */
              historyTitle={tool.name}
              resultEmpty={`当前工具：${tool.name}。确认参数后点「${tool.name}」开始处理，同一工具可连点多次，任务会排队并发出图。`}
              aspectRatio={form.aspectRatio}
              /* 历史记录上的「重做」把该次参数填回左栏（见 lib/refill.ts）。
                 ⚠️ 工具本身（tool）也在表单里，回填后跟着一起切回原来那个工具。 */
              onRedo={(job: Job) => {
                patch(refillFromInputs(form, job.inputs))
                toast.success('已把这次任务的参数填回左侧表单 —— 确认后可再次生成')
              }}
            />
          </>
        }
      />

      <PriceConfirmDialog info={flow.pending} onCancel={flow.cancel} onConfirm={flow.confirm} />
    </div>
  )
}
